/**
 * The landing's claim demonstrations: three small machines that DO the thing the
 * prose in the middle of that page claims.
 *
 * WHY THIS EXISTS. Chapters 3 and 4 of the landing are sentences. Your app tells
 * you whether it is fine. When something breaks you get a fix request to paste.
 * Seven words, and then you know the product. A sentence about a product is an
 * argument a reader has to take on trust. A working piece of the product, at
 * reading size, is evidence. So each of those claims gets a demonstration built
 * out of the REAL console components, which means every word on screen goes
 * through the product's own t() catalogue and through the product's own
 * precedence table, and none of it can be flattered by marketing copy that has
 * drifted from the console since it was written.
 *
 * THERE WERE FOUR OF THESE AND NOW THERE ARE THREE, on a founder review of the
 * live page on 22 Aug 2026. The verdict was that the middle of the landing
 * "argues like an engineer": a full build log with timestamps in it, a compiler
 * error code, and a line of commentary under every demonstration telling the
 * reader what they had just watched. None of that is written for a person who
 * has never deployed anything, which is the entire audience this page is for.
 *
 * SO THE CUT, AND IT IS ONE RULE APPLIED FOUR TIMES.
 *   The raw build log left the default view of the fix request and went behind
 *   that card's own "Show technical details", which is the real disclosure this
 *   product ships. That single move deletes the wall of machine text AND
 *   demonstrates the Advanced doctrine in the place a reader can press it, so
 *   the fourth demonstration, which existed only to demonstrate that doctrine,
 *   is gone rather than kept beside a card that now does its job.
 *   Every meta line went with it. "That copy button really copies. Paste it
 *   anywhere and check." "Push the button and more words appear." "Nothing you
 *   already read moves." A caption that narrates the animation the reader is
 *   watching is a caption that assumes they missed it.
 *   Each demonstration now gets AT MOST ONE short line, and that line states a
 *   fact the picture cannot state about itself. The mono note under the caption
 *   is gone as a slot, not merely emptied, because a second line under a
 *   demonstration is how the first one turns back into commentary.
 *
 * THE RULE THIS FILE FOLLOWS IS TerminalPanel.tsx's, verbatim, because it is the
 * best animation pattern in this repo: THE BASE STYLESHEET IS THE SETTLED
 * FRAME. Every animation is a `from` keyframe attached only under
 * [data-run='yes']. Turn the animations off, scroll a demo out of view, or ask
 * for reduced motion, and what is left standing is the finished picture. There
 * is never a second render path to keep in step, and reduced motion is one rule
 * rather than a branch.
 *
 * ONE ADDITION TO THAT RULE, and it is the reason these read as demonstrations
 * rather than as loading states: the settled frame here is the INTERESTING
 * frame, not the resting one. The health band rests on amber, not on green. The
 * fix request rests written, with the sentence already typed into the box that
 * receives it. A reader who never sees one frame of motion still gets the
 * picture that carries the argument.
 *
 * EACH ONE RUNS ONCE AND RESTS, which is the corridor's doctrine and the reason
 * is the same one: a visible loop reclassifies a demonstration as a screensaver,
 * and this page already has a corridor moving on the fold. The one demo that
 * steps through states, the health band, makes exactly one pass and stops on its
 * settled frame.
 *
 * AND EACH ONE HANDS ITSELF OVER. Press anything inside a demo and its timer
 * stops for good, and the controls are yours. That is courtesy, and it is also
 * what keeps the health band from being hostile: TrafficLight is a polite live
 * region, and a band that changed its sentence every four seconds for the length
 * of a visit would announce itself to a screen reader forever. One pass is three
 * announcements. A loop is a siren.
 *
 * OFFSCREEN DEMOS ARE OFF. Each one carries its own IntersectionObserver and
 * drops [data-run] when it leaves the viewport, so a page that already flies a
 * corridor is never also animating demonstrations nobody is looking at.
 *
 * THE COLOUR RULE HOLDS AND IT IS THE PRODUCT'S: the only saturated pixels in
 * these are status lamps. No red fences, no green ticks, no tinted cards.
 * The health demonstration goes one step further and offers to turn the hue off
 * entirely, which is TrafficLight.tsx's own claim, that you can turn every color
 * off and the band still answers, made checkable in one press.
 */

import type { CSSProperties, ReactNode, RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

import { dateLabel } from './DeployRow';
import { ErrorBlock } from './ErrorBlock';
import { ERRORS_ABSOLUTE, TrafficLight } from './TrafficLight';
import type { TrafficInput } from './TrafficLight';
import { Button } from '../primitives/Button';
import { Table } from '../primitives/Table';

import { t } from '../../lib/i18n/t';
import { FAILED_BUILD_LOG, FIX_REQUEST, TYPO_DIAGNOSIS } from '../../lib/mock/builds';

/* overview.css came out with the fourth demonstration. It was imported for the
   identifier plate that disclosure opened onto, about a hundred lines of rules
   the landing carried for one card, and nothing on this page renders that plate
   any more. */
import '../../styles/components/landing-demos.css';

/* ---------------------------------------------------------------- plumbing */

const REDUCE = '(prefers-reduced-motion: reduce)';

/**
 * One inline style for one cued element.
 *
 * THE INLINE STYLE RULE is TerminalPanel's and it is unchanged here: an inline
 * style may carry a number, a length, a duration or a keyframe count, and the
 * moment a property paints it belongs in the stylesheet where check-tokens and a
 * reviewer can both see it. Nothing this function returns paints. The keyframe
 * NAMES stay in the stylesheet, attached under [data-run='yes'], because that
 * attachment is what makes a resting demo render its settled frame.
 *
 * An element that the stylesheet names but that never receives a duration from
 * here runs for 0s and lands on its `to` values, which are the settled ones.
 * Forgetting a cue makes a demo look finished, never broken.
 */
function cue(at: number, duration: number, steps?: number, chars?: number): CSSProperties {
  const style: Record<string, string | number> = {
    animationDelay: `${at}ms`,
    animationDuration: `${duration}ms`,
  };
  if (steps !== undefined) style.animationTimingFunction = `steps(${steps})`;
  if (chars !== undefined) style['--n'] = chars;
  return style as unknown as CSSProperties;
}

/**
 * "Has this person asked for less motion?", answered before the first render and
 * kept live afterwards.
 *
 * The initializer matters: resolving this in an effect would give one frame in
 * which the answer is no, and one frame is enough for a demo to start a pass a
 * reader did not want. The listener is only so that flipping the setting does
 * not need a reload.
 */
function useStill(): boolean {
  const [still, setStill] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(REDUCE).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const media = window.matchMedia(REDUCE);
    const onChange = () => setStill(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return still;
}

/**
 * "Is this demo on screen right now?", as a boolean that goes both ways.
 *
 * Deliberately NOT useNearViewport, which latches true and never lets go. That
 * hook exists so a heavy lazy chunk is fetched once and kept; this one exists so
 * a demonstration nobody is looking at is not animating, which needs the false
 * edge as much as the true one.
 *
 * A browser with no IntersectionObserver gets true, because the failure a reader
 * would notice is a demo that never runs, not a demo that runs offscreen.
 */
function useOnScreen<T extends Element>(): [RefObject<T | null>, boolean] {
  const ref = useRef<T>(null);
  const [on, setOn] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setOn(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setOn(entry?.isIntersecting === true),
      // A margin rather than a threshold, because these demos are taller than a
      // phone viewport and a threshold of 0.3 on an 800px demo in a 700px window
      // can never be met, which would leave it permanently asleep.
      { threshold: 0, rootMargin: '-8% 0px -8% 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, on];
}

/** Milliseconds per revealed character. Reading pace, not typing pace. */
const TYPE_CHAR_MS = 34;

/**
 * Wrap a sentence to a character budget, because the reveal is a clip on one
 * line and a clip cannot follow a browser's own line break. TerminalPanel's,
 * copied rather than imported: that module does not export it, and it is eight
 * lines.
 */
function wrap(text: string, cols: number): string[] {
  const lines: string[] = [];
  let line = '';
  for (const word of text.split(' ')) {
    if (line === '') {
      line = word;
    } else if (line.length + 1 + word.length <= cols) {
      line = `${line} ${word}`;
    } else {
      lines.push(line);
      line = word;
    }
  }
  if (line !== '') lines.push(line);
  return lines;
}

/**
 * A sentence that types itself, one clip per line, with a caret riding the clip
 * edge. The idiom is TerminalPanel's: the clip and the caret share a delay, a
 * duration and a steps() count, so they cannot drift by a character, and both
 * animate a compositor property rather than a width.
 *
 * It is reproduced here rather than imported because TerminalPanel's version is
 * sized in cqw for a corridor card that gets scaled by a 3D transform, and this
 * one lives in a reading column at console type size.
 */
function Typed({ text, at, cols }: { text: string; at: number; cols: number }) {
  const lines = wrap(text, cols).filter((line) => line.length > 0);
  let start = at;

  return (
    <span className="lpd__typed">
      {lines.map((line, index) => {
        const delay = start;
        const duration = line.length * TYPE_CHAR_MS;
        start += duration;
        const last = index === lines.length - 1;
        const timing = cue(delay, duration, line.length, line.length);

        return (
          <span className="lpd__line" key={`${index}-${line}`} style={timing}>
            <span className="lpd__ink" style={timing}>
              {line}
            </span>
            {/* One caret per line, lit only while that line is typing. The last
                one holds, because a caret at rest is what says the box is still
                waiting for you. */}
            <i className="lpd__caret" data-cue={last ? 'hold' : undefined} style={timing} />
          </span>
        );
      })}
    </span>
  );
}

/* ------------------------------------------------------------- the frame */

export interface LandingDemoProps {
  /**
   * Replaces the one line under the demonstration. Pass a string that has
   * already been through t(). A demo ships its own where it has one to ship.
   */
  caption?: string;
  /**
   * Names the demonstration for a screen reader. Left off, the caption is the
   * name, which is what a figure does and is usually the better sentence.
   */
  label?: string;
}

interface FrameProps {
  /** Which demonstration, for the stylesheet. Never painted. */
  demo: 'health' | 'fix' | 'vocab';
  /**
   * THE ONE LINE, AND IT IS OPTIONAL BECAUSE ZERO IS A LEGAL ANSWER.
   *
   * Founder review, 22 Aug 2026: a demonstration gets at most one short line,
   * and that line may never narrate the thing the reader just watched. A
   * caption earns its place by stating a fact the picture cannot state about
   * itself. Where the picture already says everything, as the vocabulary table
   * does, the honest number of lines is none, and a slot that must be filled is
   * how a page grows commentary.
   *
   * The mono `note` that used to sit under this is gone as a slot rather than
   * merely emptied, for the same reason.
   */
  caption?: string;
  label?: string;
  /** True while animations may attach. Offscreen and reduced motion are false. */
  run: boolean;
  /** Turns every status hue in this demo into ink. The health band's control. */
  hue?: 'on' | 'off';
  controls?: ReactNode;
  children: ReactNode;
  frameRef: RefObject<HTMLElement | null>;
  /** Fired on the first press inside the demo, so its timer can stand down. */
  onHandover?: () => void;
}

function Frame({
  demo,
  caption,
  label,
  run,
  hue = 'on',
  controls,
  children,
  frameRef,
  onHandover,
}: FrameProps) {
  return (
    <figure
      className="lpd"
      data-demo={demo}
      data-run={run ? 'yes' : 'no'}
      data-hue={hue}
      aria-label={label}
      ref={frameRef}
      onPointerDownCapture={onHandover}
      onKeyDownCapture={onHandover}
    >
      <div className="lpd__stage">{children}</div>

      {/* No caption and no controls means no figcaption at all, rather than an
          empty one. An empty figcaption is a box with a gap in it, and the gap
          is the thing a reader notices. */}
      {caption || controls ? (
        <figcaption className="lpd__foot">
          {caption ? <p className="lpd__caption">{caption}</p> : null}
          {controls ? <div className="lpd__controls">{controls}</div> : null}
        </figcaption>
      ) : null}
    </figure>
  );
}

/* ============================================================ 1. THE HEALTH */

/**
 * Every demo on this page is the same app: Studio Ledger, out of the fixture,
 * whose newest deploy did not work while the app itself stayed up. That is not
 * tidiness. It means the amber band and the fix request payload are describing
 * one real situation instead of two unrelated screenshots.
 *
 * The four identifier constants that used to sit here went with the fourth
 * demonstration. They were the project id, the app name, the region and the
 * database name, and nothing on this page opens onto them any more.
 */

/** How long each health state holds. Four seconds is a read, not a glance. */
const HEALTH_HOLD_MS = 4200;

/**
 * The state the band rests on, and it is amber on purpose.
 *
 * The resting frame of a demonstration is the frame most readers will ever see,
 * so it has to be the one that carries the claim. Green and "Everything's fine"
 * proves nothing: an empty page also says nothing is wrong. Amber, with the
 * sentence naming the version still serving and the button reading "See what
 * happened", is the whole argument in one still.
 */
const HEALTH_STILL = 1;

/** How long the version that is still serving has been running. Two days. */
const RUNNING_SINCE_MS = 2 * 24 * 60 * 60 * 1000;

interface HealthState {
  /** One plain fact about the app. The cause, in a person's words. */
  fact: string;
  /**
   * The one member of the traffic light's input that this fact changes. Every
   * other member is held, so the demonstration is a controlled experiment
   * rather than three unrelated screens: one cause moves, one consequence
   * follows, and the precedence table in TrafficLight.tsx decides the rest.
   */
  change: Partial<TrafficInput>;
}

const HEALTH: readonly HealthState[] = [
  { fact: 'Nothing has gone wrong today.', change: {} },
  { fact: 'A deploy went out this morning and it did not work.', change: { lastDeployFailed: true } },
  // Exactly at the absolute threshold, so the number on screen is the number in
  // the rule rather than a comfortable distance past it.
  { fact: 'Ten requests came back with an error in the last hour.', change: { errorsLastHour: ERRORS_ABSOLUTE } },
];

function healthInput(change: Partial<TrafficInput>): TrafficInput {
  return {
    status: 'live',
    downMinutes: 0,
    lastDeployFailed: false,
    runningSince: dateLabel(new Date(Date.now() - RUNNING_SINCE_MS).toISOString()),
    failedAddOn: null,
    overLimitResource: null,
    approaching: null,
    errorsLastHour: 0,
    // The fixture's own hourly traffic for this app, and comfortably over
    // ERRORS_REQUEST_FLOOR, so the error rule is allowed to fire at all.
    requestsLastHour: 96,
    hasTraffic: true,
    hasDeploys: true,
    ...change,
  };
}

/**
 * HealthDemo. The health band, with its cause standing beside it.
 *
 * Cause and effect in one frame, which is the one thing prose cannot do. On the
 * left, a plain sentence stating one fact about the app. On the right, the real
 * band. The fact changes and the SAME band recomputes: the lamp changes shape
 * and tone, the sentence changes, and the button's label changes with it. Every
 * string on screen is produced by trafficVerdict(), the product's real
 * precedence table, so this cannot say anything the console would not.
 *
 * The band's button is inert here and the mono note says so. It is a picture of
 * a console, and a landing page that wired a live "See what happened" to nothing
 * would be teaching a reader that buttons on this product lie.
 */
export function HealthDemo({ caption, label }: LandingDemoProps) {
  const [ref, onScreen] = useOnScreen<HTMLElement>();
  const still = useStill();

  const [phase, setPhase] = useState(HEALTH_STILL);
  const [byHand, setByHand] = useState(false);
  // One pass is one visit to each of the three states and back to the frame it
  // started on, which is the amber. Then it stops, for good.
  const [left, setLeft] = useState(HEALTH.length);

  const stepping = onScreen && !still && !byHand && left > 0;

  useEffect(() => {
    if (!stepping) return;
    const id = window.setTimeout(() => {
      setPhase((n) => (n + 1) % HEALTH.length);
      setLeft((n) => n - 1);
    }, HEALTH_HOLD_MS);
    return () => window.clearTimeout(id);
  }, [stepping, left]);

  // phase is always inside the list, because every write to it goes through a
  // modulo. The fallback is there so an out of range phase would rest on the
  // settled frame rather than throw, which is this file's habit everywhere.
  const state = HEALTH[phase] ?? HEALTH[HEALTH_STILL];
  const input = healthInput(state.change);

  function stepByHand() {
    setByHand(true);
    setPhase((n) => (n + 1) % HEALTH.length);
  }

  return (
    <Frame
      demo="health"
      frameRef={ref}
      run={onScreen && !still}
      label={label}
      /* ONE LINE, AND IT IS NOT A DESCRIPTION OF THE ANIMATION. The heading
         above this demonstration already says what it claims, and the band
         below is already saying it in the product's own words, so a caption
         that read "the light changes and the words change with it" would be
         telling a reader what they were looking at while they looked at it.
         What the picture cannot say about itself is that it is not a picture:
         these are the sentences the real project page prints. That is the fact,
         it is eight words, and a ten year old can follow it. */
      caption={caption ?? t('These are the real words your project page uses.')}
      onHandover={() => setByHand(true)}
      controls={
        <Button variant="ghost" size="sm" onClick={stepByHand}>
          {t('Show the next state')}
        </Button>
      }
    >
      <div className="lpd__health">
        <div className="lpd__cause">
          <p className="lpd__eyebrow mono">{t('one fact about your app')}</p>
          {/* Keyed on the phase so it remounts and replays its arrival. The
              base stylesheet is the settled sentence; the run is a fade. */}
          <p className="lpd__cause-line" key={phase} style={cue(0, 320)}>
            {t(state.fact)}
          </p>
        </div>

        <div className="lpd__screen">
          <p className="lpd__eyebrow mono">{t('your project page')}</p>
          <div className="lpd__band" key={phase} style={cue(0, 320)}>
            <TrafficLight input={input} />
          </div>
        </div>
      </div>
    </Frame>
  );
}

/* ======================================================== 2. THE FIX REQUEST */

/**
 * The beats, in milliseconds from the moment the demonstration comes on screen.
 *
 * They are here rather than in the stylesheet for the reason TerminalPanel gives:
 * a component never names a color, and the timing of one sequence is easier to
 * argue about as a list of numbers in one place than as eight animation
 * shorthands spread down a file.
 *
 * THERE USED TO BE FIVE OF THEM AND NOW THERE ARE THREE, because two of the
 * five timed a picture that is no longer in the default view: the raw build log
 * arriving, and a token travelling a wire from that log to the card beside it.
 * The story is three beats now and each beat has one artifact, so the numbers
 * are three and they are tighter. A reader who scrolls this into view reads a
 * sentence, watches a box appear underneath it, and watches that box receive
 * the paste. Under two seconds end to end.
 */
const FIX_BEATS = {
  card: 0,
  agent: 820,
  type: 1080,
} as const;

/**
 * Characters across the agent box. It is derived, not a taste: the box is held
 * to 34ch in landing-demos.css so it fits a 320px phone at mono fs-1 without
 * the clip having to follow a browser's own line break. Move one and move both.
 */
const AGENT_COLS = 34;

/**
 * The first line of the real payload, read out of the payload itself, so this
 * box literally cannot show a line the clipboard would not.
 *
 * It does not go through t(), and that is builds.ts's own rule: a fix request is
 * machine text a coding agent reads in English, and translating the instruction
 * while the code context stayed English would make the paste worse.
 */
const AGENT_LINE = FIX_REQUEST.split('\n')[0] ?? '';

/**
 * FixRequestDemo. The fix request, made rather than described.
 *
 * THREE BEATS, ONE ARTIFACT EACH, and that shape is the founder's cut of
 * 22 Aug 2026 rather than a simplification somebody preferred.
 *
 *   1. A sentence in a person's words: "Your code has a spelling mistake in a
 *      function name." It is the real ErrorBlock's real summary, so a marketing
 *      page cannot say it more kindly than the console does.
 *   2. The Copy fix request button, which is the product's own control carrying
 *      the product's own payload. A ten year old can press it, paste it into a
 *      notes app, and check that this page was telling the truth. A homepage
 *      control that performs the product's actual job is the strongest
 *      demonstration available on a marketing page.
 *   3. The box the paste lands in, typing the first line of that payload.
 *
 * WHAT WENT, AND WHERE IT WENT. The default view used to open with the raw
 * build log: eleven lines of timestamps, a TS2551 compiler error, and a caret
 * pointing at a misspelled identifier, sitting in the leftmost column of the
 * first thing a visitor reads about failure. It is honest and it is unreadable
 * to the person this page is written for, and putting it first taught that
 * person the product was not for them before the sentence beside it could tell
 * them it was.
 *
 * It is not deleted. It is passed to ErrorBlock as `raw`, which puts it behind
 * that component's own "Show technical details" disclosure, exactly where the
 * console puts it. That is one move with two payoffs: the wall of machine text
 * leaves the default view, and the Advanced doctrine ("nothing is hidden behind
 * a friendlier word, the simple screen is the real screen and the detail is
 * added below it, moving nothing above") is now demonstrated by a control the
 * reader can press instead of by a fourth demonstration further down the page.
 * The fourth demonstration is gone. See the header.
 *
 * The wire and its travelling token went with the log, and that is a real loss
 * worth naming: a token crossing a gap SHOWS a transformation where two panels
 * side by side only imply one. It bought that at the price of the transformation
 * starting in something nobody in the audience can read. The card arriving under
 * its own heading carries the same story at a tenth of the cognitive cost.
 *
 * It runs once. There is no loop here on purpose: a reader who has just clicked
 * Copy and is watching for the check does not want the card fading out from
 * under them, so the replay is a button instead of a timer.
 */
export function FixRequestDemo({ caption, label }: LandingDemoProps) {
  const [ref, onScreen] = useOnScreen<HTMLElement>();
  const still = useStill();

  /**
   * Play it again, without remounting anything.
   *
   * Dropping [data-run] for one frame and putting it back detaches every
   * animation name and re attaches it, which is what restarts a run. A key
   * change would do it too and would also throw away the error block's "Copied"
   * state and anything the reader had opened, which is exactly the state a
   * person pressing replay is most likely to be in the middle of.
   *
   * It stays, and the founder's note said it could: the control is allowed
   * where a demonstration does not loop, and this one does not.
   */
  const [armed, setArmed] = useState(true);
  useEffect(() => {
    if (armed) return;
    const id = window.requestAnimationFrame(() => setArmed(true));
    return () => window.cancelAnimationFrame(id);
  }, [armed]);

  return (
    <Frame
      demo="fix"
      frameRef={ref}
      run={onScreen && !still && armed}
      label={label}
      /* ONE LINE, AND IT IS THE FOURTH DEMONSTRATION COMPRESSED INTO IT. The
         heading above says what happens when something breaks; the card below
         does it. What neither of them says out loud is the doctrine the
         disclosure inside the card is standing there proving, so that is the
         line. It is a fact about a control the reader can see, not a
         description of the animation they just watched. */
      caption={caption ?? t('Nothing is hidden. The technical details are one press away.')}
      controls={
        <Button variant="ghost" size="sm" onClick={() => setArmed(false)}>
          {t('Play it again')}
        </Button>
      }
    >
      <div className="lpd__fix">
        <p className="lpd__eyebrow mono">{t('what SV Cloud says')}</p>
        <div className="lpd__fix-card" style={cue(FIX_BEATS.card, 520)}>
          <ErrorBlock
            summary={t(TYPO_DIAGNOSIS.summary ?? '')}
            cause={t(TYPO_DIAGNOSIS.cause ?? '')}
            location={TYPO_DIAGNOSIS.location ?? undefined}
            fixRequest={FIX_REQUEST}
            /* The honest log, at the depth the console keeps it. rawLabel is
               passed rather than defaulted so the words a reader presses are
               written on this page and can be read in one place. */
            rawLabel={t('Show technical details')}
            raw={FAILED_BUILD_LOG}
          />
        </div>
      </div>

      {/* Where the paste lands. The terminal idiom is TerminalPanel's, at
          reading size: a sigil column, one clipped line at a time, and a caret
          that holds at the end because the box is still waiting for you. */}
      <div className="lpd__agent" style={cue(FIX_BEATS.agent, 360)}>
        <p className="lpd__eyebrow mono">{t('the tool that writes your code')}</p>
        <p className="lpd__agent-line">
          <span className="lpd__sigil" aria-hidden="true">
            {'>'}
          </span>
          <Typed text={AGENT_LINE} at={FIX_BEATS.type} cols={AGENT_COLS} />
        </p>
      </div>
    </Frame>
  );
}

/* ============================================================== 3. THE WORDS */

interface VocabRow {
  ours: string;
  theirs: string;
}

/**
 * The seven, in the landing's own order. They are held here rather than imported
 * because Landing.tsx builds them inside its component body, and a marketing
 * screen is not a module a pattern should reach into.
 */
const VOCAB: readonly VocabRow[] = [
  { ours: 'App', theirs: 'worker, service, function' },
  { ours: 'Deploy', theirs: 'release, revision, build artifact' },
  { ours: 'Web address', theirs: 'custom domain, hostname' },
  { ours: 'Database', theirs: 'instance, pool, gateway' },
  { ours: 'Files', theirs: 'object storage, bucket, blob' },
  { ours: 'Connection', theirs: 'binding, wire, integration' },
  { ours: 'Keys & settings', theirs: 'secrets, environment variables' },
];

/**
 * One cue, shared by all fourteen cells, so the whole table turns as one object.
 *
 * That is the decision in this demonstration and it is worth stating plainly:
 * NEVER row by row. A seven row ledger that reveals itself in sequence is a slot
 * machine on a page whose claim is that nothing is hidden, and Landing.tsx says
 * so in its own comment for the static version of this table. One motion, one
 * meaning, seven rows moving together.
 */
const SWEEP = cue(0, 900);

/**
 * VocabSweep. Seven words, and one turn of the whole table.
 *
 * What is present first is the crowded column, at lead weight, which is the
 * world the reader arrives from. Then it falls back to its muted final weight as
 * the plain word on the left settles into the lead position. One transition,
 * once, on entry, and then it rests forever on the finished table.
 *
 * The table is the real Table primitive, unchanged, so it is a real table
 * element with real column headers for anyone reading it with a screen reader or
 * copying it into a spreadsheet.
 */
export function VocabSweep({ caption, label }: LandingDemoProps) {
  const [ref, onScreen] = useOnScreen<HTMLElement>();
  const still = useStill();

  // Latched, unlike the other three. This one runs on the way in and never
  // again, so scrolling back up does not re play a turn the reader has seen.
  const [ran, setRan] = useState(false);
  useEffect(() => {
    if (onScreen) setRan(true);
  }, [onScreen]);

  return (
    <Frame
      demo="vocab"
      frameRef={ref}
      run={ran && !still}
      label={label}
      /* NO LINE AT ALL, AND THAT IS THE EDIT RATHER THAN AN OMISSION.
         This demonstration used to carry two: "Other clouds have many words for
         one thing. We picked one." and "Seven words. There is no glossary to go
         and learn first." They say the same thing as each other, and the
         chapter's own lede two elements above them says it a third time. One
         line survives, once, and the one that survives is the chapter's,
         because it is the one a reader meets before the table rather than
         after it.
         The table is also the one demonstration on this page that needs no help
         at all: two columns, seven rows, and a header on each column saying who
         says what. A caption under it would be a person explaining a joke. */
      caption={caption}
    >
      <div className="lpd__vocab">
        <Table<VocabRow>
          caption={t('What SV Cloud calls things')}
          rows={[...VOCAB]}
          rowKey={(row) => row.ours}
          columns={[
            {
              key: 'ours',
              header: t('SV Cloud says'),
              width: '40%',
              render: (row) => (
                <span className="lpd__vocab-ours" style={SWEEP}>
                  {t(row.ours)}
                </span>
              ),
            },
            {
              key: 'theirs',
              header: t('Everyone else says'),
              render: (row) => (
                <span className="lpd__vocab-theirs" style={SWEEP}>
                  {t(row.theirs)}
                </span>
              ),
            },
          ]}
        />
      </div>
    </Frame>
  );
}
